import React, { useState, useEffect, useCallback } from "react";
import { Upload, Trash2, PauseCircle, PlayCircle, Download, Mail, Image, Smartphone, RefreshCw } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL || "";

function AdminFileManager() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [versionName, setVersionName] = useState("");
  const [platform, setPlatform] = useState("android");
  const [totalDownloads, setTotalDownloads] = useState(0);

  const load = useCallback(() => {
    fetch(`${API}/api/app-files`).then(r => r.json()).then(setFiles).catch(() => {});
    fetch(`${API}/api/app-files/stats`).then(r => r.json()).then(d => setTotalDownloads(d.total_downloads || 0)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !versionName) return;
    setUploading(true);
    setUploadError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("version_name", versionName);
      fd.append("platform", platform);
      const res = await fetch(`${API}/api/admin/app-files/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setUploadError(err.detail || `Erreur ${res.status}`);
      } else {
        setVersionName("");
      }
    } catch (err) {
      setUploadError("Erreur réseau lors de l'upload");
    }
    setUploading(false);
    e.target.value = "";
    load();
  };

  const toggle = async (id) => {
    await fetch(`${API}/api/admin/app-files/${id}/toggle`, { method: "PUT", headers });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce fichier ?")) return;
    await fetch(`${API}/api/admin/app-files/${id}`, { method: "DELETE", headers });
    load();
  };

  const fmtSize = (b) => b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${(b / 1e3).toFixed(0)} KB`;
  const fmtDate = (d) => new Date(d).toLocaleString("fr-FR");

  return (
    <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-6" data-testid="file-manager">
      <div className="flex items-center gap-3 mb-6">
        <Smartphone className="w-5 h-5 text-[#FF6B35]" />
        <h3 className="text-lg font-bold text-white">Fichiers App (APK / IPA)</h3>
        <span className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-semibold" data-testid="total-downloads">
          <Download className="w-4 h-4" /> {totalDownloads} telechargements
        </span>
      </div>
      {uploadError && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="upload-error">
          {uploadError}
        </div>
      )}
      <div className="flex flex-wrap gap-3 mb-6">
        <input value={versionName} onChange={e => setVersionName(e.target.value)} placeholder="Nom de version (ex: v1.4.0)" className="bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 text-white text-sm placeholder:text-[#666] focus:outline-none focus:border-[#FF6B35]" data-testid="version-name-input" />
        <select value={platform} onChange={e => setPlatform(e.target.value)} className="bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 text-white text-sm" data-testid="platform-select">
          <option value="android">Android (APK)</option>
          <option value="ios">iOS (IPA)</option>
        </select>
        <label className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${!versionName || uploading ? 'bg-[#333] text-[#666] cursor-not-allowed' : 'bg-[#FF6B35] hover:bg-[#e55a25] text-white'}`}>
          <Upload className="w-4 h-4" /> {uploading ? "Upload..." : "Uploader"}
          <input type="file" accept={platform === "android" ? ".apk" : ".ipa"} onChange={upload} disabled={!versionName || uploading} className="hidden" data-testid="file-upload-input" />
        </label>
      </div>
      {files.length === 0 ? (
        <p className="text-[#666] text-sm">Aucun fichier uploade.</p>
      ) : (
        <div className="space-y-3">
          {files.map(f => (
            <div key={f.id} className={`flex items-center gap-4 p-4 rounded-lg border ${f.status === "active" ? "bg-[#0a0a0a] border-[#252525]" : "bg-[#1a1a1a] border-[#333] opacity-60"}`} data-testid={`file-${f.id}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold ${f.platform === "android" ? "bg-green-500/10 text-green-400" : "bg-blue-500/10 text-blue-400"}`}>
                {f.platform === "android" ? "APK" : "IPA"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-white text-sm truncate">{f.version_name} <span className="text-[#666]">({f.filename})</span></p>
                <p className="text-[#666] text-xs">{fmtSize(f.file_size)} - {fmtDate(f.uploaded_at)} - {f.status === "active" ? "Actif" : "Suspendu"}</p>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#B0B0B0] flex items-center gap-1"><Download className="w-3 h-3" />{f.downloads}</span>
                <button onClick={() => toggle(f.id)} className="p-1.5 rounded-lg hover:bg-[#252525] transition-colors" title={f.status === "active" ? "Suspendre" : "Activer"} data-testid={`toggle-${f.id}`}>
                  {f.status === "active" ? <PauseCircle className="w-4 h-4 text-yellow-400" /> : <PlayCircle className="w-4 h-4 text-green-400" />}
                </button>
                <button onClick={() => del(f.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors" data-testid={`delete-file-${f.id}`}>
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminScreenshots() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [shots, setShots] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/api/screenshots`).then(r => r.json()).then(setShots).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const upload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    for (let i = 0; i < files.length; i++) {
      const fd = new FormData();
      fd.append("file", files[i]);
      fd.append("order", String(shots.length + i));
      await fetch(`${API}/api/admin/screenshots/upload`, { method: "POST", headers, body: fd });
    }
    setUploading(false);
    load();
  };

  const del = async (id) => {
    await fetch(`${API}/api/admin/screenshots/${id}`, { method: "DELETE", headers });
    load();
  };

  return (
    <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-6" data-testid="screenshot-manager">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Image className="w-5 h-5 text-[#FF6B35]" />
          <h3 className="text-lg font-bold text-white">Screenshots App</h3>
        </div>
        <label className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B35] hover:bg-[#e55a25] text-white text-sm font-medium cursor-pointer transition-colors">
          <Upload className="w-4 h-4" /> {uploading ? "Upload..." : "Ajouter"}
          <input type="file" accept="image/*" multiple onChange={upload} disabled={uploading} className="hidden" data-testid="screenshot-upload-input" />
        </label>
      </div>
      {shots.length === 0 ? (
        <p className="text-[#666] text-sm">Aucun screenshot. Les placeholders seront affiches.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
          {shots.map(s => (
            <div key={s.id} className="relative group rounded-lg overflow-hidden border border-[#333]" data-testid={`shot-${s.id}`}>
              <img src={s.url.startsWith("http") ? s.url : `${API}${s.url}`} alt="" className="w-full aspect-[9/16] object-cover" />
              <button onClick={() => del(s.id)} className="absolute top-1 right-1 p-1 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-shot-${s.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminNewsletter() {
  const { token } = useAuth();
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/admin/newsletter`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setSubs).catch(() => {});
  }, [token]);

  const exportCSV = () => {
    const csv = "Email,Nom,Date\n" + subs.map(s => `${s.email},${s.name},${s.subscribed_at}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "newsletter_subscribers.csv";
    a.click();
  };

  return (
    <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-6" data-testid="newsletter-manager">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-[#FF6B35]" />
          <h3 className="text-lg font-bold text-white">Newsletter ({subs.length})</h3>
        </div>
        {subs.length > 0 && (
          <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#252525] hover:bg-[#333] text-white text-sm transition-colors" data-testid="export-csv">
            <Download className="w-4 h-4" /> Exporter CSV
          </button>
        )}
      </div>
      {subs.length === 0 ? (
        <p className="text-[#666] text-sm">Aucun inscrit.</p>
      ) : (
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {subs.map(s => (
            <div key={s.id} className="flex items-center justify-between p-3 bg-[#0a0a0a] rounded-lg border border-[#252525]">
              <div>
                <p className="text-white text-sm font-medium">{s.email}</p>
                {s.name && <p className="text-[#666] text-xs">{s.name}</p>}
              </div>
              <p className="text-[#666] text-xs">{new Date(s.subscribed_at).toLocaleDateString("fr-FR")}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BackOfficePage() {
  return (
    <div className="space-y-6" data-testid="backoffice-page">
      <h2 className="text-2xl font-bold text-white">Back Office - Landing Page</h2>
      <AdminFileManager />
      <AdminScreenshots />
      <AdminNewsletter />
    </div>
  );
}
